<template>
    <Layout>
        <PageHeader :title="$t('module.meetingRoomBooking')" :items="breadcrumbs" />
        <div class="card">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-lg-4 mb-2 mb-lg-0">
                        <b-input-group>
                            <b-form-input v-model.trim="searchText" type="search" :placeholder="$t('label.search')"
                                @keyup.enter="applyFilters" />
                            <b-input-group-append>
                                <b-button variant="info" @click="applyFilters">
                                    <i class="mdi mdi-magnify"></i>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                    <div class="col-lg-8 d-flex justify-content-lg-end">
                        <b-dropdown variant="warning" no-caret right boundary="viewport">
                            <template #button-content>
                                <i class="mdi mdi-filter"></i>
                            </template>
                            <div class="px-3 py-2" @wheel.stop @touchmove.stop>
                                <div class="card">
                                    <b-form-group :label="$t('label.company')">
                                        <multiselect v-model="company" track-by="value" label="text"
                                            :options="companyDdl" :allow-empty="true" @input="companyChanged" />
                                    </b-form-group>
                                    <b-form-group :label="$t('label.office')">
                                        <multiselect v-model="office" track-by="value" label="text" :options="officeDdl"
                                            :allow-empty="true" :disabled="!company" @input="officeChanged" />
                                    </b-form-group>
                                    <b-form-group :label="$t('module.meetingRoom')">
                                        <multiselect v-model="meetingRoom" track-by="value" label="text"
                                            :options="meetingRoomDdl" :allow-empty="true" :loading="meetingRoomLoading"
                                            @search-change="searchMeetingRooms" />
                                    </b-form-group>
                                    <div class="row">
                                        <div class="col-6">
                                            <b-form-group :label="$t('label.meetingDateStart')"><b-form-input
                                                    v-model="filters.meetingDateStart" type="date" /></b-form-group>
                                        </div>
                                        <div class="col-6">
                                            <b-form-group :label="$t('label.meetingDateEnd')"><b-form-input
                                                    v-model="filters.meetingDateEnd" type="date" /></b-form-group>
                                        </div>
                                    </div>
                                    <b-form-group :label="$t('label.status')"><b-form-select v-model="filters.isActive"
                                            :options="activeOptions" /></b-form-group>
                                    <div class="d-flex justify-content-end">
                                        <b-button size="sm" variant="outline-secondary" @click="resetFilters">{{
                                            $t("label.reset")
                                        }}</b-button><b-button size="sm" variant="info" class="ml-2"
                                            @click="applyFilters">{{ $t("label.search")
                                            }}</b-button>
                                    </div>
                                </div>
                            </div>
                        </b-dropdown>
                    </div>
                </div>
                <div v-if="!status.list" class="text-center p-5 m-5">
                    <b-spinner variant="info" class="m-5" />
                </div>
                <template v-else>
                    <b-table :items="rows" :fields="fields" show-empty responsive no-local-sorting
                        @sort-changed="changeSort"><template #cell(companyName)="{ item }"><CompanyLink :uuid="item.companyUUID" :name="item.companyName" /></template><template #cell(bookingUsers)="{ item }">
                            <div v-if="item.bookingUsers.length">
                                <div>{{ item.bookingUsers[0].name || '-' }}</div>
                                <small class="text-muted">{{ item.bookingUsers[0].email || '-' }}</small>
                                <div v-if="item.bookingUsers.length > 1">
                                    <b-button variant="link" size="sm" class="p-0" @click="showBookingUsers(item)">
                                        +{{ item.bookingUsers.length - 1 }} {{ $t('label.more') }}
                                    </b-button>
                                </div>
                            </div><span v-else>-</span>
                        </template><template #cell(isActive)="{ item }"><b-badge
                                :variant="item.isActive ? 'success' : 'secondary'">{{
                                    $t(item.isActive ? "label.active" : "label.inactive")
                                }}</b-badge></template><template #cell(actions)="{ item }"><a href="javascript:void(0)"
                                class="text-info" @click="openDetail(item)"><i
                                    class="mdi mdi-eye-outline font-size-18"></i></a></template></b-table>
                    <div v-if="pagination.total"
                        class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
                        <small>{{
                            $tc("label.results", pagination.total, [pagination.total])
                            }}</small><b-pagination v-model="page" :total-rows="pagination.total"
                            :per-page="criteria.pageSize" class="mb-0" @change="go" />
                    </div>
                </template>
            </div>
        </div>
        <b-modal id="meeting-room-booking-detail" :title="$t('label.bookingDetails')" size="lg" hide-footer centered
            @hidden="closeDetail">
            <MeetingRoomBookingDetail v-if="detailVisible" :booking-u-u-i-d="selectedUUID" />
        </b-modal>
        <b-modal id="meeting-room-booking-users" :title="$t('label.bookingUsers')" hide-footer centered
            @hidden="selectedBookingUsers = []">
            <div v-for="(user, index) in selectedBookingUsers" :key="user.uuid || index" class="border-bottom py-2">
                <div>{{ user.name || '-' }}</div>
                <small class="text-muted">{{ user.email || '-' }}</small>
            </div>
        </b-modal>
    </Layout>
</template>
<script>
import { mapState } from "vuex";
import Multiselect from "vue-multiselect";
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { meetingRoomBookingMethods, lookupMethods } from "@/state/api";
import { MEETING_ROOM_BOOKING_MODULE } from "@/state/api/meetingRoomBooking.state";
import MeetingRoomBookingDetail from "./detail";

const dateWithOffset = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const defaults = () => ({
    pageNumber: 1,
    pageSize: 15,
    searchArray: [],
    meetingRoomUUID: "",
    companyUUID: "",
    officeUUID: "",
    meetingDateStart: dateWithOffset(0),
    meetingDateEnd: dateWithOffset(3),
    isActive: "",
    sortBy: "created_at",
    orderBy: "desc",
});
export default {
    name: "MeetingRoomBooking",
    page: { title: i18n.t("module.meetingRoomBooking") },
    components: { Layout, PageHeader, Multiselect, MeetingRoomBookingDetail },
    data() {
        return {
            searchText: "",
            page: 1,
            criteria: defaults(),
            filters: defaults(),
            company: null,
            office: null,
            meetingRoom: null,
            meetingRoomLoading: false,
            meetingRoomTimer: null,
            detailVisible: false,
            selectedUUID: "",
            selectedBookingUsers: [],
            breadcrumbs: [
                {
                    text: i18n.t("module.meetingRoomManagement"),
                    href: "/meeting-room-management",
                },
                { text: i18n.t("module.meetingRoomBooking"), active: true },
            ],
            fields: [
                { key: "meetingRoomName", label: i18n.t("module.meetingRoom") },
                { key: "companyName", label: i18n.t("label.company") },
                { key: "bookingUsers", label: i18n.t("label.bookingUsers") },
                {
                    key: "startDisplay",
                    label: i18n.t("label.meetingStart"),
                    sortable: true,
                },
                {
                    key: "endDisplay",
                    label: i18n.t("label.meetingEnd"),
                    sortable: true,
                },
                { key: "isActive", label: i18n.t("label.status") },
                { key: "actions", label: i18n.t("label.action") },
            ],
        };
    },
    computed: {
        ...mapState(MEETING_ROOM_BOOKING_MODULE, ["status"]),
        rows() {
            return this.$store.state.api.meetingRoomBooking.list.data || [];
        },
        pagination() {
            return this.$store.state.api.meetingRoomBooking.list.pagination || {};
        },
        companyDdl() {
            return this.$store.state.api.lookup.companyDdl || [];
        },
        officeDdl() {
            return this.$store.state.api.lookup.officeDdl || [];
        },
        meetingRoomDdl() {
            return this.$store.state.api.lookup.meetingRoomDdl || [];
        },
        activeOptions() {
            return [
                { value: "", text: this.$t("label.all") },
                { value: 1, text: this.$t("label.active") },
                { value: 0, text: this.$t("label.inactive") },
            ];
        },
    },
    created() {
        this.lookupCompany();
        this.lookupMeetingRoom();
        this.load();
    },
    beforeDestroy() {
        clearTimeout(this.meetingRoomTimer);
    },
    methods: {
        ...meetingRoomBookingMethods,
        ...lookupMethods,
        load() {
            return this.getAllMeetingRoomBooking({ filter: this.criteria });
        },
        async companyChanged(value) {
            this.office = null;
            this.meetingRoom = null;
            if (value) await this.lookupOffice({ companyUUID: value.value });
            await this.loadMeetingRooms();
        },
        async officeChanged() {
            this.meetingRoom = null;
            await this.loadMeetingRooms();
        },
        loadMeetingRooms(searchWords = []) {
            return this.lookupMeetingRoom({
                companyUUID: this.company?.value || "",
                officeUUID: this.office?.value || "",
                searchWords,
            });
        },
        searchMeetingRooms(term) {
            clearTimeout(this.meetingRoomTimer);
            this.meetingRoomTimer = setTimeout(async () => {
                this.meetingRoomLoading = true;
                await this.loadMeetingRooms(term ? [term] : []);
                this.meetingRoomLoading = false;
            }, 350);
        },
        applyFilters() {
            this.page = 1;
            this.criteria = {
                ...this.filters,
                pageNumber: 1,
                pageSize: this.criteria.pageSize,
                searchArray: this.searchText ? [this.searchText] : [],
                companyUUID: this.company?.value || "",
                officeUUID: this.office?.value || "",
                meetingRoomUUID: this.meetingRoom?.value || "",
            };
            this.load();
        },
        resetFilters() {
            this.searchText = "";
            this.company = null;
            this.office = null;
            this.meetingRoom = null;
            this.filters = defaults();
            this.criteria = defaults();
            this.page = 1;
            this.load();
        },
        go(pageNumber) {
            this.page = pageNumber;
            this.criteria = { ...this.criteria, pageNumber };
            this.load();
        },
        changeSort({ sortBy, sortDesc }) {
            const map = {
                agenda: "agenda",
                startDisplay: "start",
                endDisplay: "end",
            };
            if (!map[sortBy]) return;
            this.page = 1;
            this.criteria = {
                ...this.criteria,
                pageNumber: 1,
                sortBy: map[sortBy],
                orderBy: sortDesc ? "desc" : "asc",
            };
            this.load();
        },
        openDetail(item) {
            this.selectedUUID = item.uuid;
            this.detailVisible = true;
            this.$nextTick(() => this.$bvModal.show("meeting-room-booking-detail"));
        },
        closeDetail() {
            this.detailVisible = false;
            this.selectedUUID = "";
        },
        showBookingUsers(item) {
            this.selectedBookingUsers = item.bookingUsers;
            this.$nextTick(() => this.$bvModal.show("meeting-room-booking-users"));
        },
    },
};
</script>